import React, { useEffect, useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import MainLayout from "../../layouts/MainLayout";
import Tabs from "../../components/Tabs";
import Alert from "../../components/Alert";
import { createLookupApi } from "../../api/lookupApi";
import { listClassConfigurations } from "../../api/classConfigurationApi";
import {
  listSubHeadConfigs,
  feeSubHeadRemoveRoster,
  feeSubHeadRemove,
  listRemovedFeeSubHeads,
  reAssignFeeSubHeads,
} from "../../api/feeApi";
import "../Attendance/Attendance.css";

const academicYearApi = createLookupApi("academic-years");
const feeHeadApi = createLookupApi("fee-heads");

/**
 * Direct port of digicampusbd's real `fee-subHead-remove`/`fee-subHead-removeList`
 * ("Fee Remove" / "Fee Remove List") — two tabs sharing one search scope
 * (year + section + fee head), same as the old app's own real two-tab bar.
 * Confirmed via the old app's own real templates and
 * `StudentFeeSubHeadDeleteService`.
 * <p>
 * <b>These two real urls previously pointed at this migration's "Fee Head
 * Exemption" tab</b> (`StudentFeeHeadDiscardInfo`, a different real entity —
 * a blanket per-fee-head exemption, not this one's per-sub-head zero-out) —
 * that was a misrouting, not a faithful port; see MIGRATION.md for the full
 * evidence trail (including that `StudentFeeHeadDiscardInfo` itself has no
 * discoverable real admin page anywhere in the old app).
 * <p>
 * Remove: search → roster (excludes students already fully exempted from
 * this fee head) → check students → pick sub-head(s) → zero them out (skips
 * any student who already has a real invoice-detail row for that exact
 * head+subhead — same eligibility rule as the old app).
 * Remove List: search → every zero-paid invoice-detail row for this
 * scope → select + "Re-Assign" (undo) individually or in bulk.
 */
export default function FeeSubHeadRemovePage() {
  const { pageUrl } = useParams();
  const history = useHistory();
  const tab = pageUrl === "fee-subHead-removeList" ? "removeList" : "remove";

  const [academicYears, setAcademicYears] = useState([]);
  const [academicYear, setAcademicYear] = useState("");
  const [classConfigs, setClassConfigs] = useState([]);
  const [classConfigId, setClassConfigId] = useState("");
  const [feeHeads, setFeeHeads] = useState([]);
  const [feeHeadId, setFeeHeadId] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    academicYearApi.list().then((years) => {
      setAcademicYears(years);
      if (years.length > 0) setAcademicYear(years[0].name);
    });
    listClassConfigurations().then((configs) => {
      setClassConfigs(configs);
      if (configs.length > 0) setClassConfigId(String(configs[0].id));
    });
    feeHeadApi.list().then(setFeeHeads);
  }, []);

  const filters = { academicYear, classConfigId, feeHeadId };

  return (
    <MainLayout>
      <div className="page-header">
        <h1>Fee Remove</h1>
      </div>
      <Tabs
        tabs={[
          { key: "remove", label: "Remove" },
          { key: "removeList", label: "Remove List" },
        ]}
        active={tab}
        onChange={(key) => history.push(key === "removeList" ? "/fee-subHead-removeList" : "/fee-subHead-remove")}
      />
      <Alert type="error" onDismiss={() => setError("")}>
        {error}
      </Alert>
      <Alert type="success" onDismiss={() => setSuccess("")}>
        {success}
      </Alert>

      <div className="attendance-filters">
        <label>
          Select Year
          <select value={academicYear} onChange={(e) => setAcademicYear(e.target.value)}>
            {academicYears.map((y) => (
              <option key={y.id} value={y.name}>
                {y.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Select Section
          <select value={classConfigId} onChange={(e) => setClassConfigId(e.target.value)}>
            {classConfigs.map((c) => (
              <option key={c.id} value={c.id}>
                {c.classConfigName}
              </option>
            ))}
          </select>
        </label>
        <label>
          Select Fee Head
          <select value={feeHeadId} onChange={(e) => setFeeHeadId(e.target.value)} required>
            <option value="" disabled>
              Select
            </option>
            {feeHeads.map((h) => (
              <option key={h.id} value={h.id}>
                {h.name}
              </option>
            ))}
          </select>
        </label>
      </div>

      {tab === "remove" ? (
        <RemoveTab filters={filters} setError={setError} setSuccess={setSuccess} />
      ) : (
        <RemoveListTab filters={filters} setError={setError} setSuccess={setSuccess} />
      )}
    </MainLayout>
  );
}

function RemoveTab({ filters, setError, setSuccess }) {
  const { academicYear, classConfigId, feeHeadId } = filters;
  const [students, setStudents] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [feeSubHeads, setFeeSubHeads] = useState([]);
  const [selectedSubHeadIds, setSelectedSubHeadIds] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showPicker, setShowPicker] = useState(false);

  useEffect(() => {
    if (!feeHeadId) {
      setFeeSubHeads([]);
      return;
    }
    listSubHeadConfigs(feeHeadId).then((data) => setFeeSubHeads(data.map((r) => ({ id: r.feeSubHeadId, name: r.feeSubHeadName }))));
    setSelectedSubHeadIds([]);
  }, [feeHeadId]);

  const handleSearch = async () => {
    setError("");
    setSuccess("");
    if (!academicYear || !classConfigId || !feeHeadId) {
      setError("Please select a year, section, and fee head.");
      return;
    }
    setSearching(true);
    try {
      const data = await feeSubHeadRemoveRoster(feeHeadId, classConfigId, academicYear);
      setStudents(data);
      setSelectedIds([]);
    } catch (err) {
      setError("Failed to load students.");
    } finally {
      setSearching(false);
    }
  };

  const toggleStudent = (id) => setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const toggleAll = () =>
    setSelectedIds((prev) => (students && prev.length === students.length ? [] : (students || []).map((s) => s.identificationId)));
  const toggleSubHead = (id) => setSelectedSubHeadIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const handleProcess = () => {
    if (selectedIds.length === 0) {
      setError("Please select at least one student first.");
      return;
    }
    setShowPicker(true);
  };

  const handleConfirm = async () => {
    setError("");
    setSuccess("");
    if (selectedSubHeadIds.length === 0) {
      setError("Please select at least one fee sub-head.");
      return;
    }
    try {
      const result = await feeSubHeadRemove({
        feeHeadId: Number(feeHeadId),
        feeSubHeadIds: selectedSubHeadIds.map(Number),
        identificationIds: selectedIds,
      });
      setSuccess(`${result} fee sub-head row(s) removed.`);
      setShowPicker(false);
      setSelectedSubHeadIds([]);
      handleSearch();
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to remove.");
    }
  };

  return (
    <>
      <div className="form-actions">
        <button type="button" className="btn-primary" onClick={handleSearch} disabled={searching}>
          {searching ? "Searching..." : "Search"}
        </button>
      </div>

      {students && (
        <div className="table-scroll">
          <table className="attendance-table">
            <thead>
              <tr>
                <th>
                  <input type="checkbox" checked={students.length > 0 && selectedIds.length === students.length} onChange={toggleAll} />
                </th>
                <th>Student ID</th>
                <th>Name</th>
                <th>Roll</th>
                <th>Section</th>
              </tr>
            </thead>
            <tbody>
              {students.map((s) => (
                <tr key={s.identificationId}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(s.identificationId)}
                      onChange={() => toggleStudent(s.identificationId)}
                    />
                  </td>
                  <td>{s.customStudentId}</td>
                  <td>{s.studentName}</td>
                  <td>{s.studentRoll}</td>
                  <td>{s.sectionName}</td>
                </tr>
              ))}
              {students.length === 0 && (
                <tr>
                  <td colSpan={5}>No eligible students found for this selection.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      {students && students.length > 0 && (
        <div className="form-actions">
          <button type="button" className="btn-primary" onClick={handleProcess}>
            Process
          </button>
        </div>
      )}

      {showPicker && (
        <div className="user-form" style={{ marginTop: 16 }}>
          <h3>Select Fee Sub-Head(s) to remove for {selectedIds.length} selected student(s)</h3>
          {feeSubHeads.map((s) => (
            <label key={s.id} className="checkbox-inline" style={{ display: "inline-flex", marginRight: 12 }}>
              <input type="checkbox" checked={selectedSubHeadIds.includes(String(s.id))} onChange={() => toggleSubHead(String(s.id))} />
              {s.name}
            </label>
          ))}
          <div className="form-actions">
            <button type="button" className="btn-primary" onClick={handleConfirm}>
              Save
            </button>
            <button type="button" className="btn-link" onClick={() => setShowPicker(false)}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </>
  );
}

function RemoveListTab({ filters, setError, setSuccess }) {
  const { academicYear, classConfigId, feeHeadId } = filters;
  const [rows, setRows] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [searching, setSearching] = useState(false);

  const handleSearch = async () => {
    setError("");
    setSuccess("");
    if (!academicYear || !classConfigId || !feeHeadId) {
      setError("Please select a year, section, and fee head.");
      return;
    }
    setSearching(true);
    try {
      const data = await listRemovedFeeSubHeads(feeHeadId, classConfigId, academicYear);
      setRows(data);
      setSelectedIds([]);
    } catch (err) {
      setError("Failed to load removed fee sub-heads.");
    } finally {
      setSearching(false);
    }
  };

  const toggleRow = (id) => setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const toggleAll = () => setSelectedIds((prev) => (rows && prev.length === rows.length ? [] : (rows || []).map((r) => r.detailsId)));

  const handleReAssignOne = async (detailsId) => {
    setError("");
    setSuccess("");
    try {
      const count = await reAssignFeeSubHeads([detailsId]);
      setSuccess(`${count} row(s) re-assigned.`);
      handleSearch();
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to re-assign.");
    }
  };

  const handleReAssignSelected = async () => {
    setError("");
    setSuccess("");
    if (selectedIds.length === 0) {
      setError("Please select a row first!");
      return;
    }
    try {
      const count = await reAssignFeeSubHeads(selectedIds);
      setSuccess(`${count} row(s) re-assigned.`);
      handleSearch();
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to re-assign.");
    }
  };

  return (
    <>
      <div className="form-actions">
        <button type="button" className="btn-primary" onClick={handleSearch} disabled={searching}>
          {searching ? "Searching..." : "Search"}
        </button>
      </div>

      {rows && (
        <div className="table-scroll">
          <table className="attendance-table">
            <thead>
              <tr>
                <th>
                  <input type="checkbox" checked={rows.length > 0 && selectedIds.length === rows.length} onChange={toggleAll} />
                </th>
                <th>Student ID</th>
                <th>Name</th>
                <th>Roll</th>
                <th>Fee Head</th>
                <th>Fee Sub-Head</th>
                <th>Delete</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.detailsId}>
                  <td>
                    <input type="checkbox" checked={selectedIds.includes(r.detailsId)} onChange={() => toggleRow(r.detailsId)} />
                  </td>
                  <td>{r.customStudentId}</td>
                  <td>{r.studentName}</td>
                  <td>{r.studentRoll}</td>
                  <td>{r.feeHeadName}</td>
                  <td>{r.feeSubHeadName}</td>
                  <td>
                    <button type="button" className="btn-link danger" onClick={() => handleReAssignOne(r.detailsId)}>
                      Re-Assign
                    </button>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={7}>No removed fee sub-heads found for this selection.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      {rows && rows.length > 0 && (
        <div className="form-actions">
          <button type="button" className="btn-primary" onClick={handleReAssignSelected}>
            Re-Assign Selected
          </button>
        </div>
      )}
    </>
  );
}
